import Vue from 'vue'
import Router from 'vue-router'
import axios from '@/axios.js'
import {
  TWOFA_SETUP_FLAG, isAccountantOnly, isPathAllowedForAccountant, landingPathFor,
  isClientOnly, isPathAllowedForClient, CLIENT_HOME,
  isReadOnlyUser, isReadOnlyBlockedPath, readOnlyRedirectFor, notifyReadOnlyOnce, READ_ONLY_TEXT
} from '@/helpers/roleAccess.js'

Vue.use(Router)

/** Si l'opération a un xml_macdo, redirige vers la vue firewood (view ou edit). */
function beforeEnterInvoiceRedirectToFirewood (redirectToFirewoodView) {
  return (to, from, next) => {
    const userId = to.params.userId
    if (!userId) return next()
    axios.get(`/operation/data/${userId}`)
      .then((res) => {
        if (res.data && res.data.xml_macdo) {
          const path = redirectToFirewoodView
            ? `/firewood-invoice/${userId}`
            : `/firewood-invoice-edit/${userId}`
          // query conservée : ?archivePdf=1 (page FSC) et ?fromEdit=true doivent suivre.
          next({ path, query: to.query, replace: true })
        } else {
          next()
        }
      })
      .catch(() => next())
  }
}

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,
  scrollBehavior() {
    return { x: 0, y: 0 }
  },
  routes: [

    {
      // =============================================================================
      // MAIN LAYOUT ROUTES
      // =============================================================================
      path: '',
      component: () => import('./layouts/main/Main.vue'),
      children: [
        {
          path: '/agent',
          name: 'agent-section',
          component: () => import('@/views/agent/AgentSection.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Agent', active: true }
            ],
            pageTitle: 'Agent',
            rule: 'admin'
          }
        },
        {
          // Outil jetable (non lié au menu) : correction TVA bois de feu — envoi manuel e-Factura
          path: '/outil-efactura-tva-bois',
          name: 'outil-efactura-firewood',
          component: () => import('@/views/tools/OutilEfacturaFirewood.vue'),
          meta: {
            pageTitle: 'Outil e-Factura — correction TVA bois de feu',
            rule: 'admin'
          }
        },
        {
          path: '/quotations/checkdata',
          name: 'datachecker',
          component: () => import('@/views/quotation_verif_data/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Dashboard' },
              { title: 'Check Data', active: true }
            ],
            pageTitle: 'Check Data',
            rule: 'admin'
          }
        },
        {
          path: '/configurations/edit-profil',
          name: 'user-edit-profil',
          component: () => import('@/views/configurations/profil/Profil.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Users' },
              { title: 'Edit Profil', active: true }
            ],
            pageTitle: 'Edit Profil',
            rule: 'admin'
          }
        },
        {
          path: '/configurations/users-list',
          name: 'users-list',
          component: () => import('@/views/configurations/users/DataListView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Users' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Users List',
            rule: 'admin'
          }
        },
        {
          path: '/woodfinder',
          name: 'wood-finder',
          component: () => import('@/views/woodfinder/DashboardWoodfinder.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Wood Finder', active: true },
            ],
            pageTitle: 'Wood Finder',
            rule: 'admin'
          }
        },
        {
          path: '/customerfinder',
          name: 'customer-finder',
          component: () => import('@/views/customerfinder/CustomerFinder.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Customer Finder', active: true },
            ],
            pageTitle: 'Customer Finder',
            rule: 'admin'
          }
        },
        {
          path: '/stock/fsc',
          name: 'stock-fsc',
          component: () => import('@/views/fsc/FscSummary.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Stock' },
              { title: 'FSC', active: true }
            ],
            pageTitle: 'FSC',
            rule: 'admin'
          }
        },
        {
          path: '/scale/pesees',
          name: 'scale-pesees',
          component: () => import('@/views/scale/ScaleWeighingsList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Weighings', active: true },
            ],
            pageTitle: 'Scale weighings',
            rule: 'editor'
          }
        },
        {
          path: '/budget',
          name: 'budget',
          component: () => import('@/views/budget/BudgetList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Budget List', active: true },
            ],
            pageTitle: 'Budget List',
            rule: 'admin'
          }
        },
        {
          path: '/budget/:idbudget',
          name: 'budget-view',
          component: () => import('@/views/budget/BudgetView.vue'),
          meta: {
            pageTitle: 'Budget',
            rule: 'editor'
          }
        },
        // PAYMENT SCHEDULE
        {
          path: '/paymentschedule/:idoperation',
          name: 'paymentschedule-list',
          component: () => import('@/views/paymentschedule/DataListView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Harvesting 2.0', url: '/harvesting2' },
              { title: 'Payment Schedule', active: true }
            ],
            pageTitle: 'Payment Schedule',
            rule: 'admin'
          }
        },
        {
          path: '/payslip',
          name: 'payslip-view',
          component: () => import('@/views/employees/payslip/DataListView.vue'),
          meta: {
            pageTitle: 'Payslip list',
            rule: 'admin'
          }
        },
        {
          path: '/payslipsender/:id',
          name: 'payslipsender-view',
          component: () => import('@/views/employees/payslipsender/DataListView.vue'),
          meta: {
            pageTitle: 'Payslip Sms Sender',
            rule: 'admin'
          }
        },
        // EFACTURA
        {
          path: '/kpi',
          name: '/kpi',
          component: () => import('@/views/bankaccounts/kpi.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'KPI', active: true },
            ],
            pageTitle: 'KPI',
            rule: 'admin'
          }
        },
        {
          // Bons fiscaux casa de marcat. Distinct des listes eFactura : une
          // facture electronique et un bon fiscal de caisse sont deux
          // documents differents, avec des obligations differentes.
          path: '/fiscal-jobs',
          name: 'fiscal-jobs',
          component: () => import('@/views/fiscal/FiscalJobList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Casa de Marcat', active: true },
            ],
            pageTitle: 'Casa de Marcat — Fiscal receipts',
            rule: 'admin'
          }
        },
        {
          path: '/efacturalist-out',
          name: '/efacturalist-out',
          component: () => import('@/views/efactura/out-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'e-Factura sent to ANAF', active: true },
            ],
            pageTitle: 'e-Factura sent to ANAF',
            rule: 'admin'
          }
        },
        {
          path: '/efacturalist-in',
          name: '/efacturalist-in',
          component: () => import('@/views/efactura/in-list/Pending.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eFactura Pending', active: true },
            ],
            pageTitle: 'eFactura Pending',
            rule: 'admin'
          }
        },
        {
          path: '/efacturalist-ing',
          name: '/efacturalist-ing',
          component: () => import('@/views/efactura/in-list/List.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eFactura List Provider', active: true },
            ],
            pageTitle: 'eFactura List Provider',
            rule: 'admin'
          }
        },
        {
          path: '/efacturalogin',
          name: 'efactura-login',
          component: () => import('@/views/efactura/login.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eFactura Login', active: true },
            ],
            pageTitle: 'eFactura Login',
            rule: 'admin'
          }
        },
        {
          path: '/anafcallback',
          component: () => import('@/views/efactura/logincode.vue'),
          pageTitle: 'eFactura Login',
          beforeEnter: (to, from, next) => {
            const code = to.query.code;
            console.log(code)
            next();
          }, meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eFactura Login', active: true },
            ],
            pageTitle: 'eFactura Login',
            rule: 'admin'
          }
        },
        // PAYMENT SCHEDULE
        {
          path: '/bankaccounts',
          name: 'bank-account',
          component: () => import('@/views/bankaccounts/accounts.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Bank Accounts', active: true },
            ],
            pageTitle: 'Bank Accounts',
            rule: 'admin'
          }
        },
        // SOLAR ADMIN (Restricted Area popup → /solar-admin)
        {
          path: '/solar-admin',
          name: 'solar-admin',
          component: () => import('@/views/solar-admin/SolarAdmin.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Solar Panel', active: true },
            ],
            pageTitle: 'Solar Panel',
            rule: 'admin'
          }
        },
        // BROKER CHECK (Restricted - cross-check approved quotations)
        {
          path: '/broker-check',
          name: 'broker-check',
          component: () => import('@/views/broker/BrokerCheck.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Broker Check', active: true },
            ],
            pageTitle: 'Broker Check',
            rule: 'admin'
          }
        },
        // BROKER CHECK -> PAIEMENT (Restricted - commissions des brokers, releves PDF)
        {
          path: '/broker-check/payment',
          name: 'broker-commission-payment',
          component: () => import('@/views/broker/BrokerCommissionPayment.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Broker Check', url: '/broker-check' },
              { title: 'Paiement', active: true },
            ],
            pageTitle: 'Broker Commission Payment',
            rule: 'admin'
          }
        },
        // BROKER CHECK -> BROKER STATUS (Restricted - switch « lecture seule » par compte broker)
        {
          path: '/broker-check/status',
          name: 'broker-status',
          component: () => import('@/views/broker/BrokerStatus.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Broker Check', url: '/broker-check' },
              { title: 'Status', active: true },
            ],
            pageTitle: 'Broker Status',
            rule: 'admin'
          }
        },
        // CLIENT ACCESS (Restricted - logins du portail client et fiches visibles)
        {
          path: '/client-access',
          name: 'client-access',
          component: () => import('@/views/client/admin/ClientAccess.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Client Access', active: true }
            ],
            pageTitle: 'Client Access',
            rule: 'admin'
          }
        },
        // INVOICING
        {
          path: '/invoice/invoice-list',
          name: 'invoice-list',
          component: () => import('@/views/invoicing/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Invoice List',
            rule: 'admin'
          }
        },
        {
          path: '/invoice-edit/:userId',
          name: 'app-invoice-edit',
          component: () => import('@/views/invoicing/operation-edit/OperationEditTab.vue'),
          beforeEnter: beforeEnterInvoiceRedirectToFirewood(false),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/invoice/invoice-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Invoice Edit',
            rule: 'editor'
          }
        },
        {
          path: '/invoice-add',
          name: 'app-invoice-add',
          component: () => import('@/views/invoicing/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/invoice/invoice-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Invoice Add',
            rule: 'editor'
          }
        },
        {
          path: '/invoice/firewood-list',
          name: 'invoice-firewood-list',
          component: () => import('@/views/invoicing/firewood-list/FirewoodList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/invoice/invoice-list' },
              { title: 'Firewood', active: true }
            ],
            pageTitle: 'Firewood',
            rule: 'admin'
          }
        },
        {
          path: '/firewood-invoice/:userId',
          name: 'firewood-invoice-view',
          component: () => import('@/views/invoicing/FirewoodInvoiceView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/invoice/invoice-list' },
              { title: 'Firewood Invoice', active: true }
            ],
            pageTitle: 'Firewood Invoice View',
            rule: 'editor'
          },
        },
        {
          path: '/firewood-invoice-edit/:userId',
          name: 'app-firewood-invoice-edit',
          component: () => import('@/views/invoicing/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/invoice/invoice-list' },
              { title: 'Firewood', url: '/invoice/firewood-list' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Firewood Invoice Edit',
            rule: 'editor'
          },
        },
        {
          path: '/invoice/:userId',
          name: 'invoice/invoice-view',
          component: () => import('@/views/invoicing/OperationView.vue'),
          beforeEnter: beforeEnterInvoiceRedirectToFirewood(true),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/invoice/invoice-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Invoice View',
            rule: 'editor'
          },
        },
        // MENU CONFIG
        {
          path: '/settings/menu',
          name: 'settings-menu',
          component: () => import('@/views/configurations/menu/menucfg.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings' },
              { title: 'Menu', active: true }
            ],
            pageTitle: 'Settings Menu',
            rule: 'admin'
          }
        },
        // La page « Rapport Harvesting » a été fusionnée dans Dependency Sync
        // (destinataires du rapport du matin, aperçu, envoi immédiat) le 2026-10-06,
        // puis ces mails sont passés dans Notifications le 2026-10-18.
        { path: '/settings/harvesting-report', redirect: '/settings/notifications' },
        {
          // Les robots dont l'ERP dépend (SumalX1n : avize ; SumalX2n : fiches
          // dépôt ; SagaX1n / SagaX2n à venir) : état, journal, réglages, heures,
          // destinataires de leur mail, mode d'emploi. Administrateurs. Les autres
          // mails sont dans /settings/notifications depuis le 2026-10-18.
          path: '/settings/dependency-sync',
          name: 'settings-dependency-sync',
          component: () => import('@/views/configurations/sumal/DependencySync.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Dependency Sync', active: true }
            ],
            pageTitle: 'Dependency Sync',
            rule: 'admin'
          }
        },
        // Ancienne adresse de cette page (liens dans les mails de synchro déjà envoyés).
        { path: '/settings/sumal-sync', redirect: '/settings/dependency-sync' },
        {
          path: '/settings/shopify-config',
          name: 'shopify-config',
          component: () => import('@/views/configurations/shopify/ShopifyConfig.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Shopify Config', active: true }
            ],
            pageTitle: 'Shopify Config',
            rule: 'admin'
          }
        },
        {
          path: '/settings/shopify-sync',
          name: 'shopify-sync',
          component: () => import('@/views/configurations/shopify/ShopifySync.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Shopify Sync', active: true }
            ],
            pageTitle: 'Shopify Sync',
            rule: 'admin'
          }
        },
        {
          path: '/settings/fiscal-config',
          name: 'fiscal-config',
          component: () => import('@/views/configurations/fiscal/FiscalConfig.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Casa de Marcat Datecs', active: true }
            ],
            pageTitle: 'Casa de Marcat Datecs',
            rule: 'admin'
          }
        },
        {
          // Tout ce qui touche aux mails, en onglets (2026-10-18) : mails
          // automatiques, mail check, liens du mail de devis, historique des
          // envois. Les anciennes pages redirigent sur leur onglet.
          path: '/settings/notifications',
          name: 'settings-notifications',
          component: () => import('@/views/configurations/notifications/Notifications.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Notifications', active: true }
            ],
            pageTitle: 'Notifications',
            rule: 'admin'
          }
        },
        { path: '/settings/mail-history', name: 'mail-history', redirect: { path: '/settings/notifications', query: { tab: 'history' } } },
        { path: '/settings/quote-email-links', name: 'quote-email-links', redirect: { path: '/settings/notifications', query: { tab: 'quote-links' } } },
        {
          // La clé Claude de l'ERP (agent /agent, lecture des contrats d'APV).
          path: '/settings/ai',
          name: 'settings-ai',
          component: () => import('@/views/configurations/ai/AiSettings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'AI', active: true }
            ],
            pageTitle: 'AI',
            rule: 'admin'
          }
        },
        {
          // Inventaire du réseau usine (caméras, NVR, équipements). Scan relayé au
          // serveur XYLO VISION qui seul a le tunnel WireGuard vers le LAN.
          path: '/settings/network',
          name: 'settings-network',
          component: () => import('@/views/configurations/network/NetworkDevices.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Network', active: true }
            ],
            pageTitle: 'Network',
            rule: 'admin'
          }
        },
        {
          path: '/outlay-add-simple',
          name: 'app-outlay-add-simple',
          component: () => import('@/views/outlay/operation-add-simple/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eFactura List Provider', url: '/efacturalist-ing/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Expenses Uploader',
            rule: 'editor'
          }
        },
        {
          path: '/configurations/outlays-list',
          name: 'outlays-list',
          component: () => import('@/views/configurations/outlays/DataListView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Expense Categories', active: true }
            ],
            pageTitle: 'Expense Categories',
            rule: 'admin'
          }
        },
        // EMPLOYEES
        {
          path: '/employees/usages/:year/:month',
          name: 'page-usages',
          component: () => import('@/views/employees/reporting/usage/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Employees' },
              { title: 'Usages', active: true }
            ],
            pageTitle: 'Equipment Usages List',
            rule: 'admin'
          }
        },
        {
          path: '/employees/employees-list',
          name: 'employees-list',
          component: () => import('@/views/employees/contracts/DataListView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Employees' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Employees List',
            rule: 'admin'
          }
        },
        {
          path: '/employees/report/:idperson',
          name: 'employee-view',
          component: () => import('@/views/employees/reporting/perperson/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Employees', url: '/employees/employees-list' },
              { title: 'Attendance Report', active: true }
            ],
            pageTitle: 'Attendance Report',
            rule: 'admin'
          }
        },
        {
          path: '/employees/monthly-report/:year/:month',
          name: 'attendances-view',
          component: () => import('@/views/employees/reporting/permonth/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Employees', url: '/employees/employees-list' },
              { title: 'Attendance Report', active: true }
            ],
            pageTitle: 'Attendance Report',
            rule: 'admin'
          }
        },
        // PEOPLE
        {
          path: '/mailings',
          name: 'mailings',
          component: () => import('@/views/mailings/Mailings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Mailings', active: true }
            ],
            pageTitle: 'Mailings',
            rule: 'admin'
          }
        },
        {
          path: '/people/potential-interest',
          name: 'people-potential-interest',
          component: () => import('@/views/people/potential-interest/PotentialInterest.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/people/people-list' },
              { title: 'Potential interest', active: true }
            ],
            pageTitle: 'Potential interest',
            rule: 'admin'
          }
        },
        {
          path: '/people/cleandb',
          name: 'people-cleandb',
          component: () => import('@/views/people/cleandb/cleanlist.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Clean People DB', active: true }
            ],
            pageTitle: 'Clean People DB',
            rule: 'admin'
          }
        },
        {
          path: '/people/reseller-map',
          name: 'reseller-map',
          component: () => import('@/views/people/UserResellerMap.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: 'people-list' },
              { title: 'Map of Clients', active: true }
            ],
            pageTitle: 'Map of Clients',
            rule: 'admin'
          }
        },
        {
          path: '/people/people-list',
          name: 'people-list',
          component: () => import('@/views/people/user-list/UserList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People' },
              { title: 'List', active: true }
            ],
            pageTitle: 'People List',
            rule: 'admin'
          }
        },
        {
          path: '/person/:userId',
          name: 'people/person-view',
          component: () => import('@/views/people/UserView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/people/people-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Person View',
            rule: 'editor'
          }
        },
        {
          path: '/person-distance/:userId',
          name: 'people/person-distance-view',
          component: () => import('@/views/people/UserDistance.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/people/people-list' },
              { title: 'Distance', active: true }
            ],
            pageTitle: 'Person Distance View',
            rule: 'editor'
          }
        },
        {
          path: '/person-edit/:userId',
          name: 'app-user-edit',
          component: () => import('@/views/people/user-edit/UserEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/people/people-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Person Edit',
            rule: 'editor'
          }
        },
        {
          path: '/person-add',
          name: 'app-person-add',
          component: () => import('@/views/people/user-add/UserEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/people/people-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Person Add',
            rule: 'editor'
          }
        },
        {
          path: '/product/product-list',
          name: 'product-list',
          component: () => import('@/views/product/product/DataListView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Product', active: true }
            ],
            pageTitle: 'Product List',
            rule: 'admin'
          }
        },
        {
          path: '/product/product-category-list',
          name: 'product-category-list',
          component: () => import('@/views/product/productcategory/DataListView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Settings', url: '/settings/menu' },
              { title: 'Category', active: true }
            ],
            pageTitle: 'Product Category List',
            rule: 'admin'
          }
        },
        {
          path: '/stock/alert',
          name: 'stock-alert',
          component: () => import('@/views/stock/StockAlert.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Stock' },
              { title: 'Alert', active: true }
            ],
            pageTitle: 'Stock Alert',
            rule: 'admin'
          }
        },
        // =============================================================================
        // REPORTS (section « Rapoarte » de la comptable ; ouvrable par admin/moderator)
        // =============================================================================
        {
          path: '/reports/centralizator',
          name: 'reports-centralizator',
          component: () => import('@/views/reports/Centralizator.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Reports' },
              { title: 'Centralizator', active: true }
            ],
            pageTitle: 'Centralizator',
            rule: 'editor'
          }
        },
        {
          path: '/stock/stock-list',
          name: 'stock-list',
          component: () => import('@/views/stock/stock-list/StockList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Stock' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Stock List',
            rule: 'admin'
          }
        },
        {
          path: '/stock/stock-history',
          name: 'stock-history',
          component: () => import('@/views/stock/stock-list/StockHistory.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Stock' },
              { title: 'History', active: true }
            ],
            pageTitle: 'Stock History',
            rule: 'admin'
          }
        },
        {
          path: '/stock/:productid',
          name: 'stock',
          component: () => import('@/views/stock/StockView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Stock', url: '/stock/stock-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Stock View',
            rule: 'admin'
          }
        },
        // OUTLAY
        {
          path: '/outlay/dashboard',
          name: 'outlay/dashboard-view',
          component: () => import('@/views/outlay/Dashboard.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Expenses', url: '/outlay/outlay-list' },
              { title: 'Dashboard', active: true }
            ],
            pageTitle: 'Dashboard',
            rule: 'admin'
          }
        },
        {
          path: '/outlay/outlay-list',
          name: 'outlay-list',
          component: () => import('@/views/outlay/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Expenses' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Expenses List',
            rule: 'admin'
          }
        },
        {
          path: '/outlay-edit/:userId',
          name: 'app-outlay-edit',
          component: () => import('@/views/outlay/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Expenses', url: '/outlay/outlay-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Expense Edit',
            rule: 'editor'
          }
        },
        {
          path: '/outlay-add',
          name: 'app-outlay-add',
          component: () => import('@/views/outlay/operation-add/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Expenses', url: '/outlay/outlay-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Expense Add',
            rule: 'editor'
          }
        },
        {
          path: '/outlay/:userId',
          name: 'outlay/outlay-view',
          component: () => import('@/views/outlay/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'EFactura Provider', url: '/efacturalist-ing' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Expense View',
            rule: 'editor'
          }
        },
        {
          path: '/outlayboneur/:userId',
          name: 'outlay/outlay-eurbon-view',
          component: () => import('@/views/outlay/OperationViewEurBon.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'EFactura Provider', url: '/efacturalist-ing' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Expense View',
            rule: 'editor'
          }
        },
        // TRANSPORT
        {
          path: '/etransport/new/:idtransport/:idinvoice',
          name: 'etransport-new-id',
          component: () => import('@/views/etransport/newetransport.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eTransport' },
              { title: 'New', active: true }
            ],
            pageTitle: 'eTransport New',
            rule: 'admin'
          }
        },
        {
          path: '/etransport/new',
          name: 'etransport-new',
          component: () => import('@/views/etransport/newetransport.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eTransport' },
              { title: 'New', active: true }
            ],
            pageTitle: 'eTransport New',
            rule: 'admin'
          }
        },
        {
          path: '/etransport/list',
          name: 'etransport-list',
          component: () => import('@/views/etransport/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eTransport' },
              { title: 'List', active: true }
            ],
            pageTitle: 'eTransport List',
            rule: 'admin'
          }
        },
        {
          path: '/transport/transport-list',
          name: 'transport-list',
          component: () => import('@/views/transport/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Transport List',
            rule: 'admin'
          }
        },
        // TRANSPORT ENTRANT

        {
          path: '/incoming-transport-edit/:userId',
          name: 'app-incoming-transport-edit',
          component: () => import('@/views/transport_entrant/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport', url: '/transport/transport-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Incoming Transport Edit',
            rule: 'editor'
          }
        },
        {
          path: '/incoming-transport-add',
          name: 'app-incoming-transport-add',
          component: () => import('@/views/transport_entrant/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport', url: '/transport/transport-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Incoming Transport Add',
            rule: 'editor'
          }
        },
        {
          path: '/incoming-transport/:userId',
          name: 'incoming-transport/transport-view',
          component: () => import('@/views/transport_entrant/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport', url: '/transport/transport-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Incoming Transport View',
            rule: 'editor'
          }
        },
        // TRANSPORT VERS CLIENT
        {
          path: '/transport-edit/:userId',
          name: 'app-transport-edit',
          component: () => import('@/views/transport/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport', url: '/transport/transport-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Transport Edit',
            rule: 'editor'
          }
        },
        {
          path: '/transport-add',
          name: 'app-transport-add',
          component: () => import('@/views/transport/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport', url: '/transport/transport-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Transport Add',
            rule: 'editor'
          }
        },
        {
          path: '/transport/:userId',
          name: 'transport/transport-view',
          component: () => import('@/views/transport/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport', url: '/transport/transport-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Transport View',
            rule: 'editor'
          }
        },
        {
          path: '/transport/reception/:userId',
          name: 'transport/transport-reception-view',
          component: () => import('@/views/transport/DownloadAllReception.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Transport', url: '/transport/transport-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Transport View',
            rule: 'editor'
          }
        },
        // RECEPTION
        {
          path: '/reception/reception-list',
          name: 'reception-list',
          component: () => import('@/views/reception/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Reception' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Reception List',
            rule: 'admin'
          }
        },
        {
          path: '/reception-edit/:userId',
          name: 'app-reception-edit',
          component: () => import('@/views/reception/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Reception', url: '/reception/reception-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Reception Edit',
            rule: 'editor'
          }
        },
        {
          path: '/reception-add',
          name: 'app-reception-add',
          component: () => import('@/views/reception/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Reception', url: '/reception/reception-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Reception Add',
            rule: 'editor'
          }
        },
        {
          path: '/reception/:userId',
          name: 'reception/reception-view',
          component: () => import('@/views/reception/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Reception', url: '/reception/reception-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Reception View',
            rule: 'editor'
          }
        },
        {
          path: '/reception/copyproduct/:idperson/:idoperationto',
          name: 'copy-product-reception',
          component: () => import('@/views/reception/CopyProduct.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Reception' },
              { title: 'Copy Product', active: true }
            ],
            pageTitle: 'Copy Product',
            rule: 'admin'
          }
        },
        // SALE
        {
          path: '/sale/sale-list',
          name: 'sale-list',
          component: () => import('@/views/sale/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Sale' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Sale List',
            rule: 'admin'
          }
        },
        {
          path: '/sale-edit/:userId',
          name: 'app-sale-edit',
          component: () => import('@/views/sale/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Sale', url: '/sale/sale-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Sale Edit',
            rule: 'editor'
          }
        },
        {
          path: '/sale-add',
          name: 'app-sale-add',
          component: () => import('@/views/sale/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Sale', url: '/sale/sale-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Sale Add',
            rule: 'editor'
          }
        },
        {
          path: '/sale/:userId',
          name: 'sale/sale-view',
          component: () => import('@/views/sale/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Sale', url: '/sale/sale-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Sale View',
            rule: 'editor'
          }
        },
        {
          path: '/sale/copyproduct/:idperson/:idoperationto',
          name: 'copy-product-sale',
          component: () => import('@/views/sale/CopyProduct.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Sale' },
              { title: 'Copy Product', active: true }
            ],
            pageTitle: 'Copy Product',
            rule: 'admin'
          }
        },
        // TRANSFORMATION
        {
          path: '/production/production-list',
          name: 'production-list',
          component: () => import('@/views/production/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Production' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Production List',
            rule: 'admin'
          }
        },
        {
          path: '/production/analysis',
          name: 'production-analysis',
          component: () => import('@/views/production/analysis/ProductionAnalysis.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Production', url: '/production/production-list' },
              { title: 'Barcode analysis', active: true }
            ],
            pageTitle: 'Barcode Analysis',
            rule: 'admin'
          }
        },
        {
          path: '/production/analysis-camera',
          name: 'production-analysis-camera',
          component: () => import('@/views/production/analysis/CameraAnalysis.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Production', url: '/production/production-list' },
              { title: 'Camera analysis', active: true }
            ],
            pageTitle: 'Camera Analysis',
            rule: 'admin'
          }
        },
        {
          path: '/production-edit/:userId',
          name: 'app-production-edit',
          component: () => import('@/views/production/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Production', url: '/production/production-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Production Edit',
            rule: 'editor'
          }
        },
        {
          path: '/production-add',
          name: 'app-production-add',
          component: () => import('@/views/production/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Production', url: '/production/production-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Production Add',
            rule: 'editor'
          }
        },
        {
          path: '/production/:userId',
          name: 'production/production-view',
          component: () => import('@/views/production/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Production', url: '/production/production-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Production View',
            rule: 'editor'
          }
        },
        // INVENTORY
        {
          path: '/inventory/inventory-list',
          name: 'inventory-list',
          component: () => import('@/views/inventory/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Inventory' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Inventory List',
            rule: 'admin'
          }
        },
        {
          path: '/inventory-edit/:userId',
          name: 'app-inventory-edit',
          component: () => import('@/views/inventory/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Inventory', url: '/inventory/inventory-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Inventory Edit',
            rule: 'editor'
          }
        },
        {
          path: '/inventory-add',
          name: 'app-inventory-add',
          component: () => import('@/views/inventory/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Inventory', url: '/inventory/inventory-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Inventory Add',
            rule: 'editor'
          }
        },
        {
          path: '/inventory/:userId',
          name: 'inventory/inventory-view',
          component: () => import('@/views/inventory/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Inventory', url: '/inventory/inventory-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Inventory View',
            rule: 'editor'
          }
        },
        // quotation
        {
          path: '/quotation/quotation-list',
          name: 'quotation-list',
          component: () => import('@/views/quotation/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Quotation' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Quotation List',
            rule: 'admin'
          }
        },
        {
          path: '/quotation/:userId',
          name: 'quotation/operation-view',
          component: () => import('@/views/quotation/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Quotation', url: '/quotation/quotation-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Quotation View',
            rule: 'editor'
          }
        },
        {
          path: '/quotation-edit/:userId',
          name: 'app-quotation-edit',
          component: () => import('@/views/quotation/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Quotation', url: '/quotation/quotation-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Quotation Edit',
            rule: 'editor'
          }
        },
        {
          path: '/quotation-add',
          name: 'app-quotation-add',
          component: () => import('@/views/quotation/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Quotation', url: '/quotation/quotation-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Quotation Add',
            rule: 'editor'
          }
        },
        {
          path: '/quotation-add-idpers/:personId',
          name: 'app-quotation-add-idpers',
          component: () => import('@/views/quotation/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Quotation', url: '/quotation/quotation-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Quotation Add',
            rule: 'editor'
          }
        },
        // purchase order
        {
          path: '/purchase/purchase-list',
          name: 'purchase-list',
          component: () => import('@/views/purchase/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Purchase Order' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Purchase Order List',
            rule: 'admin'
          }
        },
        // ENCODER LES APV PAR LEUR CONTRAT — atteint UNIQUEMENT par le bouton
        // « Encode » du rapport Harvesting du matin (aucune entrée de menu).
        // Hors de /harvesting2, réservé aux admins : ici admins + modérateurs (Florent
        // est modérateur) ; le contrôle réel est dans la page et dans l'API,
        // `rule` ne restreint personne mais doit exister (sinon vue-acl fige la navigation).
        {
          path: '/apv-encode',
          name: 'apv-encode',
          component: () => import('@/views/harvesting2/ApvEncode.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'APV contracts to encode', active: true }
            ],
            pageTitle: 'APV contracts to encode',
            rule: 'editor'
          }
        },
        // HARVESTING 2.0 (menu de gauche → Operations → Harvesting 2.0) — Admin only.
        // Plus de rideau PIN ni de tuile Restricted Area depuis le 2026-10-27 : le
        // module remplace /dashboard/harvesting, qui redirige ici.
        {
          path: '/harvesting2',
          component: () => import('@/views/harvesting2/HarvestingLock.vue'),
          meta: { rule: 'admin' },
          children: [
            {
              path: '',
              name: 'harvesting2',
              component: () => import('@/views/harvesting2/Overview.vue'),
              meta: {
                breadcrumb: [
                  { title: 'Home', url: '/' },
                  { title: 'Harvesting 2.0', active: true }
                ],
                pageTitle: 'Harvesting 2.0',
                rule: 'admin'
              }
            },
            {
              path: 'parcels',
              name: 'harvesting2-parcels',
              component: () => import('@/views/harvesting2/ParcelList.vue'),
              meta: {
                breadcrumb: [
                  { title: 'Home', url: '/' },
                  { title: 'Harvesting 2.0', url: '/harvesting2' },
                  { title: 'Les APV', active: true }
                ],
                pageTitle: 'Harvesting 2.0 — Les APV',
                rule: 'admin'
              }
            },
            {
              path: 'parcel/:apv',
              name: 'harvesting2-parcel',
              component: () => import('@/views/harvesting2/ParcelDetail.vue'),
              meta: {
                breadcrumb: [
                  { title: 'Home', url: '/' },
                  { title: 'Harvesting 2.0', url: '/harvesting2' },
                  { title: 'Un APV', active: true }
                ],
                pageTitle: 'Harvesting 2.0 — Un APV',
                rule: 'admin'
              }
            },
            {
              // L'écran « APV SUMAL » a fusionné avec la liste des APV : les deux
              // interrogeaient le même endpoint et rendaient deux tableaux de
              // douze colonnes. Le nom de route survit en redirection — il est
              // lié depuis le tableau de bord et peut être en favori.
              path: 'sumal',
              name: 'harvesting2-sumal',
              redirect: { name: 'harvesting2-parcels', query: { vue: 'sumal' } }
            },
            {
              path: 'quality',
              name: 'harvesting2-quality',
              component: () => import('@/views/harvesting2/DataQuality.vue'),
              meta: {
                breadcrumb: [
                  { title: 'Home', url: '/' },
                  { title: 'Harvesting 2.0', url: '/harvesting2' },
                  { title: 'Qualité & synchro', active: true }
                ],
                pageTitle: 'Harvesting 2.0 — Qualité & synchro',
                rule: 'admin'
              }
            }
          ]
        },
        // ACCOUNTING (Restricted Area → /accounting) — Admin only
        {
          path: '/accounting',
          name: 'accounting',
          component: () => import('@/views/purchase/accounting/AccountingView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Finance', active: true }
            ],
            pageTitle: 'Finance',
            rule: 'admin'
          }
        },
        {
          path: '/purchase/:userId',
          name: 'purchase/purchase-view',
          component: () => import('@/views/purchase/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Purchase Order', url: '/purchase/purchase-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Purchase Order View',
            rule: 'editor'
          }
        },
        {
          path: '/purchase-edit/:userId',
          name: 'app-purchase-edit',
          component: () => import('@/views/purchase/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Purchase Order', url: '/purchase/purchase-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Purchase Order Edit',
            rule: 'editor'
          }
        },
        {
          path: '/purchase-add',
          name: 'app-purchase-add',
          component: () => import('@/views/purchase/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Purchase Order', url: '/purchase/purchase-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Purchase Order Add',
            rule: 'editor'
          }
        },
        {
          path: '/calendar',
          name: 'app-calendar',
          component: () => import('@/views/calendar/SimpleCalendar.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calendar', active: true }
            ],
            pageTitle: 'Calendar',
            rule: 'editor'
          }
        },
        {
          path: '/calendarv2',
          name: 'app-calendar-v2',
          component: () => import('@/views/calendar/CalendarV2.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Calendar', active: true }
            ],
            pageTitle: 'Calendar',
            rule: 'editor'
          }
        },
        // =============================================================================
        // Theme Routes
        // =============================================================================
        {
          path: '/',
          redirect: '/dashboard/analytics'
        },
        /* BROKER*/
        {
          path: '/broker/dashboard',
          name: 'dashboard-broker',
          component: () => import('./views/broker/dashboard/Dashboard.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/broker/dashboard/finance',
          name: 'dashboard--finance-broker',
          component: () => import('./views/broker/dashboard/DashboardFinance.vue'),
          meta: {
            rule: 'editor'
          }
        },
        // quotation
        {
          path: '/broker/invoice/invoice-list',
          name: 'invoice-list-broker',
          component: () => import('@/views/broker/invoicing/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Invoice List',
            rule: 'admin'
          }
        },
        {
          path: '/broker/invoice-edit/:userId',
          name: 'app-invoice-edit-broker',
          component: () => import('@/views/broker/invoicing/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/broker/invoice/invoice-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Invoice Edit',
            rule: 'editor'
          }
        },
        {
          path: '/broker/invoice/:userId',
          name: 'invoice/invoice-view-broker',
          component: () => import('@/views/broker/invoicing/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Invoice', url: '/broker/invoice/invoice-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Invoice View',
            rule: 'editor'
          },
        },
        {
          path: '/broker/quotation/quotation-list',
          name: 'quotation-list-broker',
          component: () => import('@/views/broker/quotation/operation-list/OperationList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/broker/dashboard/' },
              { title: 'Quotation' },
              { title: 'List', active: true }
            ],
            pageTitle: 'Quotation List',
            rule: 'admin'
          }
        },
        {
          path: '/broker/quotation/:userId',
          name: 'quotation/operation-view-broker',
          component: () => import('@/views/broker/quotation/OperationView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/broker/dashboard' },
              { title: 'Quotation', url: '/broker/quotation/quotation-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Quotation View',
            rule: 'editor'
          }
        },
        {
          path: '/broker/quotation-edit/:userId',
          name: 'app-quotation-edit-broker',
          component: () => import('@/views/broker/quotation/operation-edit/OperationEditTab.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/broker/dashboard' },
              { title: 'Quotation', url: '/broker/quotation/quotation-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Quotation Edit',
            rule: 'editor'
          }
        },
        {
          path: '/broker/quotation-add',
          name: 'app-quotation-add-broker',
          component: () => import('@/views/broker/quotation/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/broker/dashboard' },
              { title: 'Quotation', url: '/broker/quotation/quotation-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Quotation Add',
            rule: 'editor'
          }
        },
        {
          path: '/broker/quotation-add-idpers/:personId',
          name: 'app-quotation-add-idpers-broker',
          component: () => import('@/views/broker/quotation/operation-add/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/broker/dashboard' },
              { title: 'Quotation', url: '/broker/quotation/quotation-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Quotation Add',
            rule: 'editor'
          }
        },

        {
          path: '/broker/people/people-list',
          name: 'people-list-broker',
          component: () => import('@/views/broker/people/user-list/UserList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People' },
              { title: 'List', active: true }
            ],
            pageTitle: 'People List',
            rule: 'admin'
          }
        },
        {
          path: '/broker/person/:userId',
          name: 'people/person-view-broker',
          component: () => import('@/views/broker/people/UserView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/broker/people/people-list' },
              { title: 'View', active: true }
            ],
            pageTitle: 'Person View',
            rule: 'editor'
          }
        },
        {
          path: '/broker/person-edit/:userId',
          name: 'app-user-edit-broker',
          component: () => import('@/views/broker/people/user-edit/UserEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/people/people-list/' },
              { title: 'Edit', active: true }
            ],
            pageTitle: 'Person Edit',
            rule: 'editor'
          }
        },
        {
          path: '/broker/person-add',
          name: 'app-person-add-broker',
          component: () => import('@/views/broker/people/user-add/UserEdit.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'People', url: '/broker/people/people-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Person Add',
            rule: 'editor'
          }
        },

        {
          path: '/dashboard/analytics',
          name: 'dashboard-analytics',
          component: () => import('./views/DashboardAnalytics.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/dashboard/map',
          name: 'dashboard-map',
          component: () => import('./views/DashboardMap.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/dashboard/analytics',
          name: 'dashboard-analytics',
          component: () => import('./views/DashboardAnalytics.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/dashboard/finance',
          name: 'dashboard-finance',
          component: () => import('./views/DashboardFinance.vue'),
          meta: {
            rule: 'admin'
          }
        },
        {
          path: '/dashboard/quotation',
          name: 'dashboard-quotation',
          component: () => import('./views/DashboardQuotation.vue'),
          meta: {
            rule: 'admin'
          }
        },
        // L'ancien tableau de bord Harvesting est désactivé (2026-10-27) : Harvesting 2.0
        // a pris sa place dans le menu. La redirection garde les favoris et les liens
        // vivants ; DashboardHarvesting.vue reste dans le dépôt, non routé.
        {
          path: '/dashboard/harvesting',
          name: 'dashboard-harvesting',
          redirect: '/harvesting2'
        },
        {
          path: '/dashboard/transport',
          name: 'dashboard-transport',
          component: () => import('./views/DashboardTransport.vue'),
          meta: {
            rule: 'admin'
          }
        },
        // =============================================================================
        // Application Routes
        // =============================================================================
        {
          path: '/apps/todo',
          redirect: '/apps/todo/all',
          name: 'todo'
        },
        {
          path: '/apps/todo/:filter',
          component: () => import('./views/apps/todo/Todo.vue'),
          meta: {
            rule: 'editor',
            parent: 'todo',
            no_scroll: true
          }
        },
        {
          path: '/apps/chat',
          name: 'chat',
          component: () => import('./views/apps/chat/Chat.vue'),
          meta: {
            rule: 'editor',
            no_scroll: true
          }
        },
        {
          path: '/apps/email',
          redirect: '/apps/email/inbox',
          name: 'email'
        },
        {
          path: '/apps/email/:filter',
          component: () => import('./views/apps/email/Email.vue'),
          meta: {
            rule: 'editor',
            parent: 'email',
            no_scroll: true
          }
        },
        {
          path: '/apps/calendar/vue-simple-calendar',
          name: 'calendar-simple-calendar',
          component: () => import('./views/apps/calendar/SimpleCalendar.vue'),
          meta: {
            rule: 'editor',
            no_scroll: true
          }
        },
        {
          path: '/apps/eCommerce/shop',
          name: 'ecommerce-shop',
          component: () => import('./views/apps/eCommerce/ECommerceShop.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eCommerce' },
              { title: 'Shop', active: true }
            ],
            pageTitle: 'Shop',
            rule: 'editor'
          }
        },
        {
          path: '/apps/eCommerce/wish-list',
          name: 'ecommerce-wish-list',
          component: () => import('./views/apps/eCommerce/ECommerceWishList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eCommerce', url: '/apps/eCommerce/shop' },
              { title: 'Wish List', active: true }
            ],
            pageTitle: 'Wish List',
            rule: 'editor'
          }
        },
        {
          path: '/apps/eCommerce/checkout',
          name: 'ecommerce-checkout',
          component: () => import('./views/apps/eCommerce/ECommerceCheckout.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eCommerce', url: '/apps/eCommerce/shop' },
              { title: 'Checkout', active: true }
            ],
            pageTitle: 'Checkout',
            rule: 'editor'
          }
        },
        /*
                  Below route is for demo purpose
                  You can use this route in your app
                    {
                        path: '/apps/eCommerce/item/',
                        name: 'ecommerce-item-detail-view',
                        redirect: '/apps/eCommerce/shop',
                    }
                */
        {
          path: '/apps/eCommerce/item/',
          redirect: '/apps/eCommerce/item/5546604'
        },
        {
          path: '/apps/eCommerce/item/:item_id',
          name: 'ecommerce-item-detail-view',
          component: () => import('./views/apps/eCommerce/ECommerceItemDetailView.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eCommerce' },
              { title: 'Shop', url: { name: 'ecommerce-shop' } },
              { title: 'Item Details', active: true }
            ],
            parent: 'ecommerce-item-detail-view',
            pageTitle: 'Item Details',
            rule: 'editor'
          }
        },
        {
          path: '/apps/user/user-list',
          name: 'app-user-list',
          component: () => import('@/views/apps/user/user-list/UserList.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'User' },
              { title: 'List', active: true }
            ],
            pageTitle: 'User List',
            rule: 'editor'
          }
        },
        // =============================================================================
        // UI ELEMENTS
        // =============================================================================
        {
          path: '/ui-elements/grid/vuesax',
          name: 'grid-vuesax',
          component: () => import('@/views/ui-elements/grid/vuesax/GridVuesax.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Grid' },
              { title: 'Vuesax', active: true }
            ],
            pageTitle: 'Grid',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/grid/tailwind',
          name: 'grid-tailwind',
          component: () => import('@/views/ui-elements/grid/tailwind/GridTailwind.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Grid' },
              { title: 'Tailwind', active: true }
            ],
            pageTitle: 'Tailwind Grid',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/colors',
          name: 'colors',
          component: () => import('./views/ui-elements/colors/Colors.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Colors', active: true }
            ],
            pageTitle: 'Colors',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/card/basic',
          name: 'basic-cards',
          component: () => import('./views/ui-elements/card/CardBasic.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Card' },
              { title: 'Basic Cards', active: true }
            ],
            pageTitle: 'Basic Cards',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/card/statistics',
          name: 'statistics-cards',
          component: () => import('./views/ui-elements/card/CardStatistics.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Card' },
              { title: 'Statistics Cards', active: true }
            ],
            pageTitle: 'Statistics Card',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/card/analytics',
          name: 'analytics-cards',
          component: () => import('./views/ui-elements/card/CardAnalytics.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Card' },
              { title: 'Analytics Card', active: true }
            ],
            pageTitle: 'Analytics Card',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/card/card-actions',
          name: 'card-actions',
          component: () => import('./views/ui-elements/card/CardActions.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Card' },
              { title: 'Card Actions', active: true }
            ],
            pageTitle: 'Card Actions',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/card/card-colors',
          name: 'card-colors',
          component: () => import('./views/ui-elements/card/CardColors.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Card' },
              { title: 'Card Colors', active: true }
            ],
            pageTitle: 'Card Colors',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/table',
          name: 'table',
          component: () => import('./views/ui-elements/table/Table.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Table', active: true }
            ],
            pageTitle: 'Table',
            rule: 'editor'
          }
        },
        {
          path: '/ui-elements/ag-grid-table',
          name: 'ag-grid-table',
          component: () => import('./views/ui-elements/ag-grid-table/AgGridTable.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'agGrid Table', active: true }
            ],
            pageTitle: 'agGrid Table',
            rule: 'editor'
          }
        },

        // =============================================================================
        // COMPONENT ROUTES
        // =============================================================================
        {
          path: '/components/alert',
          name: 'component-alert',
          component: () => import('@/views/components/vuesax/alert/Alert.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Alert', active: true }
            ],
            pageTitle: 'Alert',
            rule: 'editor'
          }
        },
        {
          path: '/components/avatar',
          name: 'component-avatar',
          component: () => import('@/views/components/vuesax/avatar/Avatar.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Avatar', active: true }
            ],
            pageTitle: 'Avatar',
            rule: 'editor'
          }
        },
        {
          path: '/components/breadcrumb',
          name: 'component-breadcrumb',
          component: () => import('@/views/components/vuesax/breadcrumb/Breadcrumb.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Breadcrumb', active: true }
            ],
            pageTitle: 'Breadcrumb',
            rule: 'editor'
          }
        },
        {
          path: '/components/button',
          name: 'component-button',
          component: () => import('@/views/components/vuesax/button/Button.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Button', active: true }
            ],
            pageTitle: 'Button',
            rule: 'editor'
          }
        },
        {
          path: '/components/button-group',
          name: 'component-button-group',
          component: () => import('@/views/components/vuesax/button-group/ButtonGroup.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Button Group', active: true }
            ],
            pageTitle: 'Button Group',
            rule: 'editor'
          }
        },
        {
          path: '/components/chip',
          name: 'component-chip',
          component: () => import('@/views/components/vuesax/chip/Chip.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Chip', active: true }
            ],
            pageTitle: 'Chip',
            rule: 'editor'
          }
        },
        {
          path: '/components/collapse',
          name: 'component-collapse',
          component: () => import('@/views/components/vuesax/collapse/Collapse.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Collapse', active: true }
            ],
            pageTitle: 'Collapse',
            rule: 'editor'
          }
        },
        {
          path: '/components/dialogs',
          name: 'component-dialog',
          component: () => import('@/views/components/vuesax/dialogs/Dialogs.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Dialogs', active: true }
            ],
            pageTitle: 'Dialogs',
            rule: 'editor'
          }
        },
        {
          path: '/components/divider',
          name: 'component-divider',
          component: () => import('@/views/components/vuesax/divider/Divider.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Divider', active: true }
            ],
            pageTitle: 'Divider',
            rule: 'editor'
          }
        },
        {
          path: '/components/dropdown',
          name: 'component-drop-down',
          component: () => import('@/views/components/vuesax/dropdown/Dropdown.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Dropdown', active: true }
            ],
            pageTitle: 'Dropdown',
            rule: 'editor'
          }
        },
        {
          path: '/components/list',
          name: 'component-list',
          component: () => import('@/views/components/vuesax/list/List.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'List', active: true }
            ],
            pageTitle: 'List',
            rule: 'editor'
          }
        },
        {
          path: '/components/loading',
          name: 'component-loading',
          component: () => import('@/views/components/vuesax/loading/Loading.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Loading', active: true }
            ],
            pageTitle: 'Loading',
            rule: 'editor'
          }
        },
        {
          path: '/components/navbar',
          name: 'component-navbar',
          component: () => import('@/views/components/vuesax/navbar/Navbar.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Navbar', active: true }
            ],
            pageTitle: 'Navbar',
            rule: 'editor'
          }
        },
        {
          path: '/components/notifications',
          name: 'component-notifications',
          component: () => import('@/views/components/vuesax/notifications/Notifications.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Notifications', active: true }
            ],
            pageTitle: 'Notifications',
            rule: 'editor'
          }
        },
        {
          path: '/components/pagination',
          name: 'component-pagination',
          component: () => import('@/views/components/vuesax/pagination/Pagination.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Pagination', active: true }
            ],
            pageTitle: 'Pagination',
            rule: 'editor'
          }
        },
        {
          path: '/components/popup',
          name: 'component-popup',
          component: () => import('@/views/components/vuesax/popup/Popup.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Popup', active: true }
            ],
            pageTitle: 'Popup',
            rule: 'editor'
          }
        },
        {
          path: '/components/progress',
          name: 'component-progress',
          component: () => import('@/views/components/vuesax/progress/Progress.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Progress', active: true }
            ],
            pageTitle: 'Progress',
            rule: 'editor'
          }
        },
        {
          path: '/components/sidebar',
          name: 'component-sidebar',
          component: () => import('@/views/components/vuesax/sidebar/Sidebar.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Sidebar', active: true }
            ],
            pageTitle: 'Sidebar',
            rule: 'editor'
          }
        },
        {
          path: '/components/slider',
          name: 'component-slider',
          component: () => import('@/views/components/vuesax/slider/Slider.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Slider', active: true }
            ],
            pageTitle: 'Slider',
            rule: 'editor'
          }
        },
        {
          path: '/components/tabs',
          name: 'component-tabs',
          component: () => import('@/views/components/vuesax/tabs/Tabs.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Tabs', active: true }
            ],
            pageTitle: 'Tabs',
            rule: 'editor'
          }
        },
        {
          path: '/components/tooltip',
          name: 'component-tooltip',
          component: () => import('@/views/components/vuesax/tooltip/Tooltip.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Tooltip', active: true }
            ],
            pageTitle: 'Tooltip',
            rule: 'editor'
          }
        },
        {
          path: '/components/upload',
          name: 'component-upload',
          component: () => import('@/views/components/vuesax/upload/Upload.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Components' },
              { title: 'Upload', active: true }
            ],
            pageTitle: 'Upload',
            rule: 'editor'
          }
        },


        // =============================================================================
        // FORMS
        // =============================================================================
        // =============================================================================
        // FORM ELEMENTS
        // =============================================================================
        {
          path: '/forms/form-elements/select',
          name: 'form-element-select',
          component: () => import('./views/forms/form-elements/select/Select.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Form Elements' },
              { title: 'Select', active: true }
            ],
            pageTitle: 'Select',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-elements/switch',
          name: 'form-element-switch',
          component: () => import('./views/forms/form-elements/switch/Switch.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Form Elements' },
              { title: 'Switch', active: true }
            ],
            pageTitle: 'Switch',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-elements/checkbox',
          name: 'form-element-checkbox',
          component: () => import('./views/forms/form-elements/checkbox/Checkbox.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Form Elements' },
              { title: 'Checkbox', active: true }
            ],
            pageTitle: 'Checkbox',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-elements/radio',
          name: 'form-element-radio',
          component: () => import('./views/forms/form-elements/radio/Radio.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Form Elements' },
              { title: 'Radio', active: true }
            ],
            pageTitle: 'Radio',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-elements/input',
          name: 'form-element-input',
          component: () => import('./views/forms/form-elements/input/Input.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Form Elements' },
              { title: 'Input', active: true }
            ],
            pageTitle: 'Input',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-elements/number-input',
          name: 'form-element-number-input',
          component: () => import('./views/forms/form-elements/number-input/NumberInput.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Form Elements' },
              { title: 'Number Input', active: true }
            ],
            pageTitle: 'Number Input',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-elements/textarea',
          name: 'form-element-textarea',
          component: () => import('./views/forms/form-elements/textarea/Textarea.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Form Elements' },
              { title: 'Textarea', active: true }
            ],
            pageTitle: 'Textarea',
            rule: 'editor'
          }
        },
        // -------------------------------------------------------------------------------------------------------------------------------------------
        {
          path: '/forms/form-layouts',
          name: 'forms-form-layouts',
          component: () => import('@/views/forms/FormLayouts.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Forms' },
              { title: 'Form Layouts', active: true }
            ],
            pageTitle: 'Form Layouts',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-wizard',
          name: 'extra-component-form-wizard',
          component: () => import('@/views/forms/form-wizard/FormWizard.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Form Wizard', active: true }
            ],
            pageTitle: 'Form Wizard',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-validation',
          name: 'extra-component-form-validation',
          component: () => import('@/views/forms/form-validation/FormValidation.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Form Validation', active: true }
            ],
            pageTitle: 'Form Validation',
            rule: 'editor'
          }
        },
        {
          path: '/forms/form-input-group',
          name: 'extra-component-form-input-group',
          component: () => import('@/views/forms/form-input-group/FormInputGroup.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Form Input Group', active: true }
            ],
            pageTitle: 'Form Input Group',
            rule: 'editor'
          }
        },

        // =============================================================================
        // Pages Routes
        // =============================================================================
        {
          path: '/pages/profile',
          name: 'page-profile',
          component: () => import('@/views/pages/Profile.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'Profile', active: true }
            ],
            pageTitle: 'Profile',
            rule: 'editor'
          }
        },
        {
          path: '/pages/user-settings',
          name: 'page-user-settings',
          component: () => import('@/views/pages/user-settings/UserSettings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'User Settings', active: true }
            ],
            pageTitle: 'Settings',
            rule: 'editor'
          }
        },
        {
          path: '/pages/faq',
          name: 'page-faq',
          component: () => import('@/views/pages/Faq.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'FAQ', active: true }
            ],
            pageTitle: 'FAQ',
            rule: 'editor'
          }
        },
        {
          path: '/pages/knowledge-base',
          name: 'page-knowledge-base',
          component: () => import('@/views/pages/KnowledgeBase.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'KnowledgeBase', active: true }
            ],
            pageTitle: 'KnowledgeBase',
            rule: 'editor'
          }
        },
        {
          path: '/pages/knowledge-base/category',
          name: 'page-knowledge-base-category',
          component: () => import('@/views/pages/KnowledgeBaseCategory.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'KnowledgeBase', url: '/pages/knowledge-base' },
              { title: 'Category', active: true }
            ],
            parent: 'page-knowledge-base',
            rule: 'editor'
          }
        },
        {
          path: '/pages/knowledge-base/category/question',
          name: 'page-knowledge-base-category-question',
          component: () => import('@/views/pages/KnowledgeBaseCategoryQuestion.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'KnowledgeBase', url: '/pages/knowledge-base' },
              { title: 'Category', url: '/pages/knowledge-base/category' },
              { title: 'Question', active: true }
            ],
            parent: 'page-knowledge-base',
            rule: 'editor'
          }
        },
        {
          path: '/pages/search',
          name: 'page-search',
          component: () => import('@/views/pages/Search.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'Search', active: true }
            ],
            pageTitle: 'Search',
            rule: 'editor'
          }
        },
        {
          path: '/pages/invoice',
          name: 'page-invoice',
          component: () => import('@/views/pages/Invoice.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Pages' },
              { title: 'Invoice', active: true }
            ],
            pageTitle: 'Invoice',
            rule: 'editor'
          }
        },

        // =============================================================================
        // CHARTS & MAPS
        // =============================================================================
        {
          path: '/charts-and-maps/charts/apex-charts',
          name: 'extra-component-charts-apex-charts',
          component: () => import('@/views/charts-and-maps/charts/apex-charts/ApexCharts.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Charts & Maps' },
              { title: 'Apex Charts', active: true }
            ],
            pageTitle: 'Apex Charts',
            rule: 'editor'
          }
        },
        {
          path: '/charts-and-maps/charts/chartjs',
          name: 'extra-component-charts-chartjs',
          component: () => import('@/views/charts-and-maps/charts/chartjs/Chartjs.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Charts & Maps' },
              { title: 'chartjs', active: true }
            ],
            pageTitle: 'chartjs',
            rule: 'editor'
          }
        },
        {
          path: '/charts-and-maps/charts/echarts',
          name: 'extra-component-charts-echarts',
          component: () => import('@/views/charts-and-maps/charts/echarts/Echarts.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Charts & Maps' },
              { title: 'echarts', active: true }
            ],
            pageTitle: 'echarts',
            rule: 'editor'
          }
        },
        {
          path: '/charts-and-maps/maps/google-map',
          name: 'extra-component-maps-google-map',
          component: () => import('@/views/charts-and-maps/maps/google-map/GoogleMap.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Charts & Maps' },
              { title: 'Google Map', active: true }
            ],
            pageTitle: 'Google Map',
            rule: 'editor'
          }
        },


        // =============================================================================
        // EXTENSIONS
        // =============================================================================
        {
          path: '/extensions/select',
          name: 'extra-component-select',
          component: () => import('@/views/components/extra-components/select/Select.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Select', active: true }
            ],
            pageTitle: 'Select',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/quill-editor',
          name: 'extra-component-quill-editor',
          component: () => import('@/views/components/extra-components/quill-editor/QuillEditor.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Quill Editor', active: true }
            ],
            pageTitle: 'Quill Editor',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/drag-and-drop',
          name: 'extra-component-drag-and-drop',
          component: () => import('@/views/components/extra-components/drag-and-drop/DragAndDrop.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Drag & Drop', active: true }
            ],
            pageTitle: 'Drag & Drop',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/datepicker',
          name: 'extra-component-datepicker',
          component: () => import('@/views/components/extra-components/datepicker/Datepicker.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Datepicker', active: true }
            ],
            pageTitle: 'Datepicker',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/datetime-picker',
          name: 'extra-component-datetime-picker',
          component: () => import('@/views/components/extra-components/datetime-picker/DatetimePicker.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extra Components' },
              { title: 'Datetime Picker', active: true }
            ],
            pageTitle: 'Datetime Picker',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/access-control',
          name: 'extra-component-access-control',
          component: () => import('@/views/components/extra-components/access-control/AccessControl.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Access Control', active: true }
            ],
            pageTitle: 'Access Control',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/i18n',
          name: 'extra-component-i18n',
          component: () => import('@/views/components/extra-components/I18n.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'I18n', active: true }
            ],
            pageTitle: 'I18n',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/carousel',
          name: 'extra-component-carousel',
          component: () => import('@/views/components/extra-components/carousel/Carousel.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Carousel', active: true }
            ],
            pageTitle: 'Carousel',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/clipboard',
          name: 'extra-component-clipboard',
          component: () => import('@/views/components/extra-components/clipboard/Clipboard.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Clipboard', active: true }
            ],
            pageTitle: 'Clipboard',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/context-menu',
          name: 'extra-component-context-menu',
          component: () => import('@/views/components/extra-components/context-menu/ContextMenu.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Context Menu', active: true }
            ],
            pageTitle: 'Context Menu',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/star-ratings',
          name: 'extra-component-star-ratings',
          component: () => import('@/views/components/extra-components/star-ratings/StarRatings.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Star Ratings', active: true }
            ],
            pageTitle: 'Star Ratings',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/autocomplete',
          name: 'extra-component-autocomplete',
          component: () => import('@/views/components/extra-components/autocomplete/Autocomplete.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Autocomplete', active: true }
            ],
            pageTitle: 'Autocomplete',
            rule: 'editor'
          }
        },
        {
          path: '/extensions/tree',
          name: 'extra-component-tree',
          component: () => import('@/views/components/extra-components/tree/Tree.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Tree', active: true }
            ],
            pageTitle: 'Tree',
            rule: 'editor'
          }
        },
        {
          path: '/import-export/import',
          name: 'import-excel',
          component: () => import('@/views/components/extra-components/import-export/Import.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Import/Export' },
              { title: 'Import', active: true }
            ],
            pageTitle: 'Import Excel',
            rule: 'editor'
          }
        },
        {
          path: '/import-export/export',
          name: 'export-excel',
          component: () => import('@/views/components/extra-components/import-export/Export.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Import/Export' },
              { title: 'Export', active: true }
            ],
            pageTitle: 'Export Excel',
            rule: 'editor'
          }
        },
        {
          path: '/import-export/export-selected',
          name: 'export-excel-selected',
          component: () => import('@/views/components/extra-components/import-export/ExportSelected.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Extensions' },
              { title: 'Import/Export' },
              { title: 'Export Selected', active: true }
            ],
            pageTitle: 'Export Excel',
            rule: 'editor'
          }
        }
      ]
    },
    // =============================================================================
    // FULL PAGE LAYOUTS
    // =============================================================================
    {
      path: '',
      component: () => import('@/layouts/full-page/FullPage.vue'),
      children: [
        // =============================================================================
        // PAGES
        // =============================================================================
        // Expenses
        {
          path: '/inputnextexpenses',
          name: 'app-outlay-add-inputnextexpenses',
          component: () => import('@/views/outlay/operation-add-simple/InputNext.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'eFactura List Provider', url: '/efacturalist-ing/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Expenses Uploader',
            rule: 'editor'
          }
        },
        {
          path: '/employees/monthly-report-2d/:year/:month',
          name: 'attendances2d-view',
          component: () => import('@/views/employees/reporting/permonthin2d/OperationList.vue'),
          meta: {
            pageTitle: 'Attendance Report 2D',
            rule: 'admin'
          }
        },
        {
          path: '/budgetv2/:idbudget',
          name: 'budget-view-2',
          component: () => import('@/views/budget/BudgetViewV2.vue'),
          meta: {
            pageTitle: 'Budget',
            rule: 'editor'
          }
        },
        {
          path: '/outlay-add-simple-fs',
          name: 'app-outlay-add-simple-fs',
          component: () => import('@/views/outlay/operation-add-simple/OperationAdd.vue'),
          meta: {
            breadcrumb: [
              { title: 'Home', url: '/' },
              { title: 'Acquisitions', url: '/outlay/outlay-list/' },
              { title: 'Add', active: true }
            ],
            pageTitle: 'Acquisitions Uploader',
            rule: 'editor'
          }
        },
        {
          path: '/download-majoration/protected-8458451512544qsfjfskfr8224f_fqi2/:userId/:newId',
          name: 'page-download-majoration',
          component: () => import('@/views/invoicing/MajorationDownload.vue'),
          meta: {
            pageTitle: 'XYLO IMPACT - DOWNLOAD INVOICE',
            rule: 'editor'
          }
        },
        {
          path: '/download-invoice/protected-8458451512544qsfjfskfr8224f_fqi2/:userId',
          name: 'page-download-invoice',
          component: () => import('@/views/invoicing/InvoiceDownload.vue'),
          meta: {
            pageTitle: 'XYLO IMPACT - DOWNLOAD INVOICE',
            rule: 'editor'
          }
        },
        {
          path: '/download-quotation/protected-8458451512544qsfjfskfr8224f_fqi2/:userId',
          name: 'page-download-quotation',
          component: () => import('@/views/quotation/QuotationDownload.vue'),
          meta: {
            pageTitle: 'XYLO IMPACT - DOWNLOAD QUOTATION',
            rule: 'editor'
          }
        },
        /* DISABLED — ancien kiosk start/stop interne (remplacé par XYLO-ATTENDANCE)
        {
          path: '/employees/attendance',
          name: 'page-attendance',
          component: () => import('@/views/employees-attendance/moduleinout/attendance.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/employees/loadingafterstartstop',
          name: 'page-loading-startstop',
          component: () => import('@/views/employees-attendance/moduleinout/loading.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/employees/loadingafterstartstop/:message',
          name: 'page-loading-startstop-msg',
          component: () => import('@/views/employees-attendance/moduleinout/loading.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/employees/startstop/:personId/:personName',
          name: 'page-startstop',
          component: () => import('@/views/employees-attendance/moduleinout/startstop.vue'),
          meta: {
            rule: 'editor'
          }
        },
        */
        {
          path: '/callback',
          name: 'auth-callback',
          component: () => import('@/views/Callback.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/payslip/:filename',
          name: 'page-payslip-public-fn',
          component: () => import('@/views/employees/payslipsender/receiver/Login.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/payslipcode',
          name: 'page-payslip-public',
          component: () => import('@/views/employees/payslipsender/receiver/LoginCode.vue'),
          meta: {
            rule: 'editor'
          }
        },
        // FORMULAIRE TRANSPORTER
        {
          path: '/transporter/forms',
          name: 'app-transporter-code-forms',
          component: () => import('@/views/transporter/Transportcode.vue'),
          meta: {
            pageTitle: 'Transport Code',
            rule: 'editor'
          }
        },
        {
          path: '/transporter/forms/:userId',
          name: 'app-transporter-forms',
          component: () => import('@/views/transporter/OperationEditTab.vue'),
          meta: {
            pageTitle: 'Transport Edit',
            rule: 'editor'
          }
        },
        {
          // SSO bridge to the Pontaj admin (requires an authenticated ERP session;
          // the global guard redirects to login first when needed, then back here).
          path: '/attendance-sso',
          name: 'attendance-sso',
          component: () => import('@/views/pages/AttendanceSso.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          // SSO bridge to XYLO-PRICE (requires an authenticated ERP session).
          path: '/pricing-sso',
          name: 'pricing-sso',
          component: () => import('@/views/pages/PriceSso.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          // QCM jetable — questionnaire colisages (PUBLIC, sans auth). Voir PUBLIC_FRONTEND_PATHS.
          path: '/colisage-questions',
          name: 'colisage-questions',
          component: () => import('@/views/pages/ColisageQCM.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/login',
          name: 'page-login',
          component: () => import('@/views/pages/login/Login.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          // Activation 2FA forcée (rôle à 2FA obligatoire) : page plein écran,
          // token de configuration seul. Pas dans PUBLIC_FRONTEND_PATHS : il
          // faut ce token (stocké comme accessToken) pour y être.
          path: '/pages/2fa-setup',
          name: 'page-2fa-setup',
          component: () => import('@/views/pages/TwoFASetup.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/register',
          name: 'page-register',
          component: () => import('@/views/pages/register/Register.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/forgot-password',
          name: 'page-forgot-password',
          component: () => import('@/views/pages/ForgotPassword.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/reset-password',
          name: 'page-reset-password',
          component: () => import('@/views/pages/ResetPassword.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/lock-screen',
          name: 'page-lock-screen',
          component: () => import('@/views/pages/LockScreen.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/comingsoon',
          name: 'page-coming-soon',
          component: () => import('@/views/pages/ComingSoon.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/error-404',
          name: 'page-error-404',
          component: () => import('@/views/pages/Error404.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/error-500',
          name: 'page-error-500',
          component: () => import('@/views/pages/Error500.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/not-authorized',
          name: 'page-not-authorized',
          component: () => import('@/views/pages/NotAuthorized.vue'),
          meta: {
            rule: 'editor'
          }
        },
        {
          path: '/pages/maintenance',
          name: 'page-maintenance',
          component: () => import('@/views/pages/Maintenance.vue'),
          meta: {
            rule: 'editor'
          }
        }
      ]
    },
    {
      // =============================================================================
      // PORTAIL CLIENT (rôle « client », lecture seule) — gabarit à part, sans le
      // menu, la barre et les modules de l'ERP. ?as=<iduser> : aperçu admin.
      // =============================================================================
      path: '/client',
      component: () => import('@/layouts/client/ClientLayout.vue'),
      children: [
        {
          path: '',
          name: 'client-orders',
          component: () => import('@/views/client/ClientDashboard.vue'),
          meta: { rule: 'editor' }
        },
        {
          path: 'quotations',
          name: 'client-quotations',
          component: () => import('@/views/client/ClientOrders.vue'),
          meta: { rule: 'editor', portalTitle: 'nav_quotations' }
        },
        // Anciennes adresses (menus « Commandes en cours » / « précédentes » regroupés dans « Devis »).
        { path: 'orders', redirect: to => ({ name: 'client-quotations', query: Object.assign({ status: 'in_progress' }, to.query) }) },
        { path: 'history', redirect: to => ({ name: 'client-quotations', query: Object.assign({ status: 'invoiced' }, to.query) }) },
        {
          path: 'invoices',
          name: 'client-invoices',
          component: () => import('@/views/client/ClientInvoices.vue'),
          meta: { rule: 'editor', portalTitle: 'nav_invoices' }
        },
        {
          path: 'order/:id',
          name: 'client-order',
          component: () => import('@/views/client/ClientDocument.vue'),
          meta: { rule: 'editor', portalKind: 'order' }
        },
        {
          path: 'invoice/:id',
          name: 'client-invoice',
          component: () => import('@/views/client/ClientDocument.vue'),
          meta: { rule: 'editor', portalKind: 'invoice' }
        },
        {
          path: 'account',
          name: 'client-account',
          component: () => import('@/views/client/ClientAccount.vue'),
          meta: { rule: 'editor', portalTitle: 'nav_account' }
        }
      ]
    },
    // Redirect to 404 page, if no match found
    {
      path: '*',
      redirect: '/pages/error-404'
    }
  ]
})

// Routes accessibles sans authentification.
const PUBLIC_FRONTEND_PATHS = [
  '/pages/login',
  '/pages/register',
  '/pages/forgot-password',
  '/pages/reset-password',
  '/pages/error-404',
  '/pages/error-500',
  '/pages/not-authorized',
  '/pages/maintenance',
  '/pages/comingsoon',
  '/pages/lock-screen',
  '/colisage-questions'             // QCM jetable colisages (page publique, sans auth)
  // DISABLED — ancien kiosk start/stop interne (remplacé par XYLO-ATTENDANCE):
  // '/employees/attendance', '/employees/startstop', '/employees/loadingafterstartstop'
]

// Ne pas garder fromEdit dans l'URL quand on quitte une page détail (quotation/invoice)
router.beforeEach((to, from, next) => {
  const path = (to.path || '').replace(/^\/web/, '')

  // Authentication guard. vue-acl gates UI rendering by role on top of this.
  const isPublic = PUBLIC_FRONTEND_PATHS.some(p => path === p || path.startsWith(p + '/'))
  const token = localStorage.getItem('accessToken')
  if (!isPublic && !token) {
    return next({ path: '/pages/login', query: { to: to.fullPath } })
  }

  // Activation 2FA forcée (rôle à 2FA obligatoire, token de configuration
  // seul) : rien d'autre que /pages/2fa-setup tant qu'elle n'est pas faite.
  if (token && !isPublic && localStorage.getItem(TWOFA_SETUP_FLAG) === '1' && path !== '/pages/2fa-setup') {
    return next('/pages/2fa-setup')
  }

  // Compte client : confiné à son portail (/client). Le serveur lui refuse
  // tout le reste (roleGate, 403 client_scope).
  if (token && !isPublic && isClientOnly() && !isPathAllowedForClient(path)) {
    return next(CLIENT_HOME)
  }
  // Portail client vu par un autre compte : seul l'aperçu admin (?as=) y a sa place.
  if (token && !isPublic && !isClientOnly() && isPathAllowedForClient(path) && path.startsWith(CLIENT_HOME) && !(to.query && to.query.as)) {
    return next(landingPathFor())
  }

  // Comptable (sans admin/moderator) : confinée à ses pages (menu + profil).
  // Le serveur refuse de toute façon le reste (roleGate) ; ici on évite une
  // page vide ou un 403 à l'écran.
  if (token && !isPublic && isAccountantOnly() && !isPathAllowedForAccountant(path)) {
    try {
      router.app.$vs.notify({
        title: 'Denied',
        text: 'Redirection',
        iconPack: 'feather',
        icon: 'icon-alert-circle',
        color: 'danger',
        position: 'bottom-right',
        time: 8000
      })
    } catch (e) { /* pas de notification avant le montage de l'app */ }
    return next(landingPathFor())
  }

  // Compte en lecture seule (Broker Status) : les pages d'ajout / d'édition du
  // portail broker renvoient vers la liste, avec le même toast neutre que le
  // serveur (qui refuserait de toute façon l'enregistrement, 403 read_only_user).
  if (token && !isPublic && isReadOnlyUser() && isReadOnlyBlockedPath(path)) {
    try { notifyReadOnlyOnce(router.app, READ_ONLY_TEXT) } catch (e) { /* avant le montage de l'app */ }
    return next(readOnlyRedirectFor(path))
  }

  if (path.indexOf('/broker') === 0) {
    try {
      if (router.app && router.app.$tours && typeof router.app.$tours['vuexyTour'] !== 'undefined' && typeof router.app.$tours['vuexyTour'].stop === 'function') {
        router.app.$tours['vuexyTour'].stop()
      }
    } catch (e) { /* ne pas bloquer la navigation */ }
  }
  if (to.query && (to.query.fromEdit === 'true' || to.query.fromEdit === true)) {
    const isQuotationOrInvoiceDetail = /^\/(broker\/)?(quotation|invoice)\/[^/]+\/?$/.test(path)
    if (!isQuotationOrInvoiceDetail) {
      const query = { ...to.query }
      delete query.fromEdit
      next({ path: to.path, query, params: to.params, replace: true })
      return
    }
  }
  next()
})

router.afterEach(() => {
  // Remove initial loading
  const appLoading = document.getElementById('loading-bg')
  if (appLoading) {
    appLoading.style.display = 'none'
  }
})

export default router
